import React, { memo, useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { Text, Transformer, Group, Rect } from 'react-konva';
import { TextBlock } from './imageTranslatorTypes.types';
import Konva from 'konva';

interface EditableTextProps {
  block: TextBlock;
  isSelected: boolean;
  isFinished: boolean;
  onChange: (blockId: string, updatedBlock: Partial<TextBlock>) => void;
  onDeleteBlock: (id: string) => void;
  onSelectBlock: (blockId: string, ctrlKey: boolean) => void;
  scale: number;
}

const EditableText: React.FC<EditableTextProps> = memo(({
  block,
  isSelected,
  isFinished,
  onChange,
  onDeleteBlock,
  onSelectBlock,
  scale = 1,
}) => {
  const textRef = useRef<Konva.Text>(null);
  const backgroundRectRef = useRef<Konva.Rect>(null);
  const transformerRef = useRef<Konva.Transformer>(null);
  const [isEditingText, setIsEdittingText] = useState(false);

  // Calculate scaled values for rendering
  const scaledBlock = useMemo(() => {
    const fontSize = block.fontSize * scale;
    const width = block.width * scale;
    const height = block.height * scale;

    return {
      ...block,
      x: block.x * scale,
      y: block.y * scale,
      width,
      height,
      fontSize,
    }
  }, [block, scale]);

  const handleTextEdit = useCallback(() => {
    if (!textRef.current) return;
    
    setIsEdittingText(true);
    
    const stage = textRef.current.getStage();
    const stageContainer = stage!.container();
  
    
    const textarea = document.createElement('textarea');
    stageContainer.appendChild(textarea);
    
    // Style textarea to match text block
    textarea.value = block.text;
    textarea.style.position = 'absolute';
    // Apply stage scale to position
    textarea.style.top = `${block.y * scale}px`;
    textarea.style.left = `${block.x * scale}px`;
    textarea.style.width = `${block.width * scale}px`;
    textarea.style.height = `${block.height * scale}px`;
    textarea.style.fontSize = `${block.fontSize * scale}px`;
    textarea.style.fontFamily = block.fontFamily;
    textarea.style.fontWeight = block.fontWeight;
    textarea.style.padding = (block.padding || 0) + 'px';
    textarea.style.margin = '0px';
    textarea.style.border = '0';
    textarea.style.overflow = 'auto';
    textarea.style.background = 'none';
    textarea.style.outline = 'none';
    textarea.style.resize = 'none';
    textarea.style.lineHeight = `${block.fontSize * scale}px`;
    textarea.style.color = block.color;
    textarea.style.transformOrigin = 'left top';
    textarea.style.transform = `rotate(${block.rotation}deg)`;
    textarea.style.whiteSpace = 'pre-wrap';
    textarea.style.wordBreak = 'break-word';

    textarea.focus();

    function removeTextarea(saveValue: boolean = true) {
      stageContainer.removeChild(textarea);
      window.removeEventListener('click', handleOutsideClick);
      setIsEdittingText(false);
      onSelectBlock(block.id, false);
      if (saveValue) {
        onChange(block.id, {
          text: textarea.value,
        });
      }
    }

    function handleOutsideClick(e: MouseEvent) {
      if (e.target !== textarea) {
        removeTextarea();
      }
    }

    textarea.addEventListener('keydown', (e) => {
      if (e.keyCode === 13 && !e.shiftKey) {
        e.preventDefault();
        removeTextarea();
      }
      if (e.keyCode === 27) {
        removeTextarea(false);
      }
    });

    setTimeout(() => {
      window.addEventListener('click', handleOutsideClick);
    });
  }, [block, onChange, onSelectBlock, scale]);

  const handleTransform = useCallback(() => {
    if (!textRef.current || !backgroundRectRef.current) return;
    const node = textRef.current;
    const backgroundRect = backgroundRectRef.current;
    
    // Get the current transform attributes
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    const rotation = node.rotation();

    // Get the position of the node
    const position = node.getAbsolutePosition();
    
    // Calculate new dimensions in unscaled coordinates
    const newWidth = Math.max(
      node.width() * scaleX, 1);
    const newHeight = Math.max(node.height() * scaleY, 1);

    // Apply the new attributes
    const newAttrs = {
      x: position.x,
      y: position.y,
      width: newWidth,
      height: newHeight,
      rotation: rotation,
      scaleX: 1,
      scaleY: 1,
    };

    // Update both shapes with new attributes
    node.setAttrs(newAttrs);
    backgroundRect.setAttrs(newAttrs);
  }, [block, onChange, scale]);

  const handleTransformEnd = useCallback(() => {
    if (!textRef.current || !backgroundRectRef.current) return;

    const node = textRef.current;
    const rect = backgroundRectRef.current;

    // Get the current transform attributes
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    const rotation = node.rotation();

    // Get the absolute position and convert back to unscaled coordinates
    const absPos = node.getAbsolutePosition();
    const newX = absPos.x / scale;
    const newY = absPos.y / scale;
    
    // Calculate new dimensions in unscaled coordinates
    const newWidth = Math.max(node.width() * scaleX / scale, 1);
    const newHeight = Math.max(node.height() * scaleY / scale, 1);

    // Apply the new attributes, converting back to unscaled coordinates
    const newAttrs = {
      x: newX,
      y: newY,
      width: newWidth,
      height: newHeight,
      rotation: rotation,
    };

    // Reset scale to 1 since we're applying the scale to width/height
    node.scaleX(1);
    node.scaleY(1);
    rect.scaleX(1);
    rect.scaleY(1);

    onChange(block.id, newAttrs);
  }, [block, onChange, scale]);

  const handleDrag = useCallback((e: Konva.KonvaEventObject<DragEvent>) => {
    const node = e.target;
    const absPos = node.getAbsolutePosition();
    backgroundRectRef.current!.position({
      x: absPos.x,
      y: absPos.y
    });
    
  }, []);

  const handleDragEnd = useCallback((e: Konva.KonvaEventObject<DragEvent>) => {
    // Get the new position from the event target
    const node = e.target;
    const absPos = node.getAbsolutePosition();
    
    // Convert the absolute position back to unscaled coordinates
    const newPos = {
      x: absPos.x / scale,
      y: absPos.y / scale,
    };
    
    // Update both shapes with new position
    if (backgroundRectRef.current) {
      backgroundRectRef.current.position({
        x: newPos.x * scale,
        y: newPos.y * scale
      });
    }
    
    onChange(block.id, newPos);
  }, [block, onChange, scale]);

  const handleClick = useCallback((e: Konva.KonvaEventObject<MouseEvent>) => {
    // Pass the ctrl key state to the selection handler
    onSelectBlock(block.id, e.evt.ctrlKey);
  }, [block.id, onSelectBlock]);

  useEffect(() => {
    if (isSelected && !isEditingText && transformerRef.current && textRef.current) {
      transformerRef.current.nodes([textRef.current]);
      transformerRef.current.getLayer()!.batchDraw();
    }
  }, [isSelected, isEditingText]);

  // Add keyboard event handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isSelected || isEditingText) return;

      const moveAmount = e.shiftKey ? 10 : 1;

      if (e.altKey) {
        // Alt key pressed - resize bounds instead of moving
        switch (e.key) {
          case 'ArrowLeft':
            e.preventDefault();
            onChange(block.id, { width: Math.max(block.width - moveAmount, 10) });
            break;
          case 'ArrowRight':
            e.preventDefault();
            onChange(block.id, { width: block.width + moveAmount });
            break;
          case 'ArrowUp':
            e.preventDefault();
            onChange(block.id, { height: Math.max(block.height - moveAmount, 10) });
            break;
          case 'ArrowDown':
            e.preventDefault();
            onChange(block.id, { height: block.height + moveAmount });
            break;
        }
      } else {
        // Normal movement
        switch (e.key) {
          case 'ArrowLeft':
            e.preventDefault();
            onChange(block.id, { x: block.x - moveAmount, y: block.y });
            break;
          case 'ArrowRight':
            e.preventDefault();
            onChange(block.id, { x: block.x + moveAmount, y: block.y });
            break;
          case 'ArrowUp':
            e.preventDefault();
            onChange(block.id, { x: block.x, y: block.y - moveAmount });
            break;
          case 'ArrowDown':
            e.preventDefault();
            onChange(block.id, { x: block.x, y: block.y + moveAmount });
            break;
          case 'Backspace':
            e.preventDefault();
            onDeleteBlock(block.id);
            break;
          case 'Escape':
            e.preventDefault();
            onSelectBlock('', false); // Changed from null to empty string to fix the type error
            break;
          case 'Enter':
            e.preventDefault();
            handleTextEdit();
            break;
        }
      }
    };

    if (isSelected && !isEditingText) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSelected, block, onChange, isEditingText, onDeleteBlock, onSelectBlock, handleTextEdit]);

  // // Add effect to automatically enter edit mode for new blocks
  // useEffect(() => {
  //   if (isSelected && block.text === 'New Text') {
  //     handleTextEdit();
  //   }
  // }, [isSelected]);

  return (
    <Group>
      <Rect
        ref={backgroundRectRef}
        x={scaledBlock.x}
        y={scaledBlock.y}
        width={scaledBlock.width}
        height={scaledBlock.height}
        rotation={block.rotation}

        fill={block.backgroundColor}
        opacity={block.backgroundOpacity}
        shadowColor={block.boxShadow?.color}
        shadowBlur={block.boxShadow?.blur}
        shadowOffset={{
          x: block.boxShadow?.offsetX || 0,
          y: block.boxShadow?.offsetY || 0
        }}
        shadowOpacity={block.boxShadow?.opacity}
        cornerRadius={block.cornerRadius || 0}
        stroke={isEditingText ? 'blue' : block.backgroundBorder?.color}
        strokeWidth={isEditingText ? 1 : block.backgroundBorder?.width || 0}
      />
      <Text
        ref={textRef}
        {...block}
        x={scaledBlock.x}
        y={scaledBlock.y}
        width={scaledBlock.width}
        height={scaledBlock.height}
        fontSize={scaledBlock.fontSize}
        opacity={block.textOpacity || 1}
        padding={block.padding || 0}
        fontStyle={`${(block.fontStyle || 'normal')} ${block.fontWeight || 'normal'}`}
        draggable={isSelected && !isEditingText}
        onClick={handleClick}
        onTap={() => isSelected && onSelectBlock(block.id, false)}
        onDblClick={handleTextEdit}
        onDragMove={handleDrag}
        onDragEnd={handleDragEnd}
        onTransform={handleTransform}
        onTransformEnd={handleTransformEnd}
        fill={block.color}
        visible={!isEditingText}
        
        
        stroke={block.textBorder?.color}
        strokeWidth={block.textBorder?.width || 0}
        fillAfterStrokeEnabled={true}
        align={block.align || 'left'}
        verticalAlign={block.verticalAlign || 'top'}
        lineHeight={block.lineHeight || 1}
      />
      {isSelected && !isEditingText && (
        <Transformer
          ref={transformerRef}
          rotateEnabled={true}
          keepRatio={false}
          boundBoxFunc={(oldBox, newBox) => {
            const minWidth = 10;
            const minHeight = 10;
            if (newBox.width < minWidth || newBox.height < minHeight) {
              return oldBox;
            }
            return newBox;
          }}
        />
      )}
    </Group>
  );
});

export default EditableText;